(() => {
  // Safe when the section asset is emitted more than once on the same page.
  if (customElements.get('ks-simple-collections')) return;
class KsSimpleCollections extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.list = this.querySelector('[data-ks-sc-list]');
    this.items = Array.from(this.querySelectorAll('[data-ks-sc-item]'));
    this.activeItem = this.querySelector('[data-ks-sc-item].active');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.interactionTimers = new Map();
    this.resizeTimer = 0;

    if (!this.list || !this.items.length) return;
    this.setupInteractions();
    this.setupCentering();
    this.setupThemeEditor();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.interactionTimers?.forEach((timer) => clearTimeout(timer));
    this.interactionTimers?.clear();
  }

  setupInteractions() {
    this.items.forEach((item) => {
      const hitArea = item.querySelector('[data-ks-sc-hit-area]') || item;
      const link = item.querySelector('a');
      const run = () => this.runInteraction(item);
      const touchStart = (event) => {
        if (event.pointerType === 'mouse') return;
        item.classList.add('is-touching');
        run();
      };
      const touchEnd = () => item.classList.remove('is-touching');

      // Same interaction contract as BN Offer Duo: stable outer hit area,
      // one 360° horizontal turn per physical mouse entry.
      hitArea.addEventListener('mouseenter', run, { signal: this.abortController.signal });
      hitArea.addEventListener('pointerdown', touchStart, { passive: true, signal: this.abortController.signal });
      hitArea.addEventListener('pointerup', touchEnd, { signal: this.abortController.signal });
      hitArea.addEventListener('pointercancel', touchEnd, { signal: this.abortController.signal });
      link?.addEventListener('focusin', run, { signal: this.abortController.signal });
    });
  }

  runInteraction(item) {
    if (!item || this.dataset.spin !== 'true') return;
    const previous = this.interactionTimers.get(item);
    if (previous) clearTimeout(previous);
    item.classList.remove('is-interacting');
    void item.offsetWidth;
    item.classList.add('is-interacting');
    const duration = this.prefersReducedMotion ? 560 : 1020;
    const timer = setTimeout(() => {
      item.classList.remove('is-interacting');
      this.interactionTimers.delete(item);
    }, duration);
    this.interactionTimers.set(item, timer);
  }

  setupCentering() {
    if (this.activeItem) this.centerActiveItem(false);
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.centerActiveItem(false), 160);
    }, { passive: true, signal: this.abortController.signal });
  }

  centerActiveItem(smooth = true) {
    if (!this.activeItem || !this.list) return;
    requestAnimationFrame(() => {
      const listRect = this.list.getBoundingClientRect();
      const itemRect = this.activeItem.getBoundingClientRect();
      const delta = (itemRect.left + itemRect.width / 2) - (listRect.left + listRect.width / 2);
      const target = this.list.scrollLeft + delta;
      this.list.scrollTo({ left: target, behavior: smooth && !this.prefersReducedMotion ? 'smooth' : 'auto' });
    });
  }

  setupThemeEditor() {
    this.addEventListener('shopify:block:select', (event) => {
      const selected = event.target?.closest?.('[data-ks-sc-item]');
      if (!selected) return;
      selected.scrollIntoView({ behavior: this.prefersReducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      this.runInteraction(selected);
    }, { signal: this.abortController.signal });
  }
}

if (!customElements.get('ks-simple-collections')) {
  customElements.define('ks-simple-collections', KsSimpleCollections);
}

})();
